<script>
/**
 * Tabela čije su kolone podatak (`columns`), pa se zaglavlje, sortiranje, poravnanje, zbirni red i otvaranje reda pišu na jednom
 * mjestu. Sadržaj ćelije daje slot `cell-<key>` ({ row, column, index, value }), a bez njega piše se vrijednost reda.
 *
 * columns: [{ key, label, sr?, hide?: "sm" | "md", nowrap?, cls? (zaglavlje, ćelija i zbir), headCls?, cellCls?, headTitle?,
 *   align?: "end" | "center", num?, muted?, name? (kolona sa nazivom: is-name + title),
 *   zero? (true: is-zero kad je vrijednost prazna ili 0; ili funkcija (row) => boolean),
 *   sortable?, sortKey? (polje po kojem se sortira, zadano key), index? (redni broj), go? (strelica na kraju reda),
 *   slot? (ime slota, zadano key: dinamičke kolone dijele jedan slot), value?(row), format?(value, row), title?(row), cellClass?(row) }]
 *
 * Zaglavlje: slot `head-<slot>` ({ column }) mijenja natpis. Sortiranje: prop `sort` ({ key, dir }) i događaj `sort` (key) – smjer
 * određuje ekran (hasTableSort). Zbirni red: prop `totals` (po ključu kolone; ćelija bez vrijednosti je prazna i bez poravnanja),
 * natpis (`totalLabel`) stoji u koloni `name`, ili u koloni `totalKey`.
 * Red: događaj `open` (row, event) na klik, Enter i razmak; `selected(row)` označava otvoren red; rowClass(row) daje stanje reda.
 *
 * variant "" (zadano) je izgled kartica/tabela dostavnih stranica: omotač .ui-table-wrap, zadnja kolona za radnju (slot `end`) ili
 * strelicu (`chevron`), slot `card` za telefon (kad korijen ljuske ima ui-compact), red .is-click.
 * variant "report" je izgled tabela izveštaja: kartica .rd-card bez karata za telefon, tabela .rd-tbl, red [data-open] i .is-selected.
 * Nazivi klasa u CLS su iz reportDesktop.scss; kad se dva rječnika spoje, mijenja se samo ta mapa.
 */
const CLS = {
  end: "is-right",
  center: "is-center",
  num: "rd-num",
  muted: "rd-muted",
  name: "is-name",
  zero: "is-zero",
  index: "rd-col-num",
  go: "rd-col-go",
};

const text = (value) => (value === null || value === undefined ? "" : String(value));

export default {
  name: "UiTable",
  props: {
    columns: { type: Array, required: true },
    rows: { type: Array, required: true },
    rowKey: { type: Function, default: (row) => row.id },
    rowClass: { type: Function, default: () => ({}) },
    chevron: { type: Boolean, default: false },
    /** gusta tabela (manji razmaci i tekst), npr. unutar kartice */
    dense: { type: Boolean, default: false },
    /** naziv zadnje kolone za čitače ekrana */
    endLabel: { type: String, default: "" },
    /** "" (dostavne stranice) ili "report" (tabele izveštaja) */
    variant: { type: String, default: "" },
    /** stanje sortiranja { key, dir } (dir 1 rastuće, -1 opadajuće); bez njega i bez slušaoca `sort` zaglavlja nisu dugmad */
    sort: { type: Object, default: null },
    /** zbirni red: vrijednosti po ključu kolone */
    totals: { type: Object, default: null },
    totalLabel: { type: String, default: "" },
    /** kolona ispod koje stoji natpis zbira (zadano kolona `name`) */
    totalKey: { type: String, default: "" },
    /** red je otvoren (označen) */
    selected: { type: Function, default: null },
    wrapClass: { type: [String, Array, Object], default: "" },
    tableClass: { type: [String, Array, Object], default: "" },
  },
  computed: {
    report() {
      return this.variant === "report";
    },
    hasEnd() {
      return !this.report && (this.chevron || Boolean(this.$scopedSlots.end));
    },
  },
  methods: {
    slotName(column) {
      return column.slot || column.key;
    },
    valueOf(column, row) {
      return column.value ? column.value(row) : row[column.key];
    },
    shown(column, value, row) {
      return text(column.format ? column.format(value, row) : value);
    },
    /** klase koje dijele zaglavlje, ćelija i zbirna ćelija kolone */
    base(column) {
      return [column.nowrap && "nw", column.hide && `ui-hide-${column.hide}`, column.go && CLS.go, column.cls];
    },
    align(column) {
      return column.align && CLS[column.align];
    },
    ariaSort(column) {
      const key = column.sortKey || column.key;
      if (!this.sort || this.sort.key !== key) {
        return null;
      }
      return this.sort.dir > 0 ? "ascending" : "descending";
    },
    arrow(column) {
      const key = column.sortKey || column.key;
      if (!this.sort || this.sort.key !== key) {
        return "";
      }
      return this.sort.dir > 0 ? "↑" : "↓";
    },
    open(row, event) {
      this.$emit("open", row, event);
    },

    renderHeadCell(h, column) {
      const slot = this.$scopedSlots[`head-${this.slotName(column)}`];
      const content = slot ? slot({ column }) : column.sr ? h("span", { class: "ui-sr" }, column.label) : column.label;
      const cls = [this.align(column), this.base(column), column.headCls, column.index && CLS.index];
      if (column.sortable && this.$listeners.sort) {
        const button = h("button", { attrs: { type: "button" }, on: { click: () => this.$emit("sort", column.sortKey || column.key) } }, [content, " ", this.arrow(column)]);
        return h("th", { class: cls, attrs: { scope: "col", title: column.headTitle, "aria-sort": this.ariaSort(column) } }, [button]);
      }
      return h("th", { class: cls, attrs: { scope: "col", title: column.headTitle } }, [content]);
    },
    renderCell(h, column, row, index) {
      const value = this.valueOf(column, row);
      const slot = this.$scopedSlots[`cell-${this.slotName(column)}`];
      let content;
      if (slot) {
        content = slot({ row, column, index, value });
      } else if (column.index) {
        content = String(index + 1);
      } else if (column.go) {
        content = h("v-icon", { props: { size: 18, color: "#8a817a" } }, "mdi-chevron-right");
      } else {
        content = this.shown(column, value, row);
      }
      const zero = typeof column.zero === "function" ? column.zero(row) : column.zero && !value;
      const cls = [
        this.align(column),
        this.base(column),
        column.cellCls,
        (column.num || column.index) && CLS.num,
        (column.muted || column.index) && CLS.muted,
        column.name && CLS.name,
        zero && CLS.zero,
        column.cellClass && column.cellClass(row),
      ];
      // naziv u koloni `name` je i title (skraćen tekst se čita u cjelini); bez vrijednosti atribut se izostavlja
      const title = column.title ? column.title(row) : column.name && value !== null && value !== undefined ? String(value) : undefined;
      return h("td", { class: cls, attrs: { title } }, [content]);
    },
    renderRow(h, row, index) {
      const cells = this.columns.map((column) => this.renderCell(h, column, row, index));
      const opens = Boolean(this.$listeners.open);
      const data = { key: this.rowKey(row) };
      if (this.report) {
        data.class = [this.selected && this.selected(row) && "is-selected", this.rowClass(row)];
        if (opens) {
          data.attrs = { "data-open": "", tabindex: "0" };
          data.on = {
            click: (event) => this.open(row, event),
            keydown: (event) => {
              if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) {
                return;
              }
              event.preventDefault();
              this.open(row, event);
            },
          };
        }
      } else {
        data.class = ["is-click", this.rowClass(row)];
        data.attrs = { tabindex: "0" };
        data.on = {
          click: (event) => this.open(row, event),
          keydown: (event) => {
            if (event.target === event.currentTarget && event.key === "Enter") {
              this.open(row, event);
            }
          },
        };
        if (this.hasEnd) {
          const end = this.$scopedSlots.end;
          cells.push(
            h("td", { class: "r ui-table__end" }, [
              end ? end({ row }) : this.chevron ? h("v-icon", { class: "ui-table__go", props: { size: 16 } }, "mdi-chevron-right") : null,
            ])
          );
        }
      }
      return h("tr", data, cells);
    },
    renderFoot(h) {
      const cells = this.columns.map((column) => {
        let content = "";
        let hasValue = false;
        if (this.totalKey ? column.key === this.totalKey : column.name) {
          content = this.totalLabel;
        } else if (!column.index && !column.go && Object.prototype.hasOwnProperty.call(this.totals, column.key)) {
          hasValue = true;
          content = this.shown(column, this.totals[column.key], this.totals);
        }
        return h("td", { class: [hasValue && this.align(column), this.base(column), hasValue && CLS.num] }, [content]);
      });
      return h("tfoot", [h("tr", cells)]);
    },
    renderTable(h) {
      const head = this.columns.map((column) => this.renderHeadCell(h, column));
      if (this.hasEnd) {
        head.push(h("th", { attrs: { scope: "col" } }, [h("span", { class: "ui-sr" }, this.endLabel)]));
      }
      const parts = [h("thead", [h("tr", head)]), h("tbody", this.rows.map((row, index) => this.renderRow(h, row, index)))];
      if (this.report && this.totals) {
        parts.push(this.renderFoot(h));
      }
      return h("table", { class: ["ui-table", this.report && "rd-tbl", this.dense && "ui-table--dense", this.tableClass] }, parts);
    },
  },
  render(h) {
    const table = this.renderTable(h);
    if (this.report) {
      return h("div", { class: ["ui-card", "rd-card", this.wrapClass] }, [table]);
    }
    const card = this.$scopedSlots.card;
    const wrap = h("div", { class: ["ui-card", "ui-table-wrap", this.wrapClass] }, [table]);
    const list = card ? h("div", { class: "ui-cardlist" }, this.rows.map((row) => h("div", { key: `c${this.rowKey(row)}` }, card({ row })))) : null;
    return h("div", [wrap, list]);
  },
};
</script>
